//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { UnisonIconType } from '@bvengineering/unison-core';

export const ACCOUNT_SETTINGS_SHEET_WIDTH = 745;
export const ADOBE_ANALYTICS_SETUP_FIELD_WIDTH = 363;
export const ADOBE_ANALYTICS_CONNECTION_ERROR =
  'An error occurred when trying to connect your Adobe Analytics account.';
export const ADOBE_ANALYTICS_INVALID_CREDENTIALS_ERROR =
  'Invalid credentials. Please double-check them and try again.';

export const ANALYTICS_SETUP_GUIDE_URL =
  'https://docs.bazaarvoice.com/articles/#!vibe/analytics-accounts';

export const ANALYTICS_ADOBE_SETUP_GUIDE_URL =
  'https://docs.bazaarvoice.com/articles/#!vibe/adobe-analytics';

export const ANALYTICS_GOOGLE_BIG_QUERY_HELP_TEXT =
  'This gives Bazaarvoice access to your BigQuery, which contains raw GA4 events. Refer to the article at the top of this page for more information.';
export const ANALYTICS_GOOGLE_BIG_QUERY_NOT_CONNECTED_ERROR =
  'BigQuery is not connected. Provide your BigQuery link below to accurately track revenue and attribute conversions in GA4.';
export const ANALYTICS_GOOGLE_NO_DIMENSIONS_ERROR =
  'This property does not have the custom Bazaarvoice dimensions. Add the custom dimensions to the property or select a different property.';
export const ANALYTICS_GOOGLE_SETUP_GUIDE_URL =
  'https://docs.bazaarvoice.com/articles/#!vibe/set-up-google-analytics';

export const ADOBE_ANALYTICS_SETUP_CONTENT = {
  articleLabel: 'Detailed steps are outlined in this article.',
  articleUrl: ANALYTICS_ADOBE_SETUP_GUIDE_URL,
  chooseDifferentProviderLabel: 'Choose a different analytics provider',
  description: 'To connect your Adobe Analytics, follow the following steps.',
  fieldLabels: {
    adobeClientId: 'Adobe Client ID',
    reportSuiteId: 'Report Suite ID',
    scopes: 'Scopes',
    sharedSecret: 'Shared Secret',
  },
  steps: [
    {
      description:
        'You may need to speak to someone in your organization that has access to it.',
      title: 'Create a project on Adobe Developer Console.',
    },
    {
      description:
        'Visit Adobe Analytics and pick an eVar, then set it up with the required fields and values.',
      title: 'Reserve and set up an eVar.',
    },
    {
      description:
        'Refer to the project in step 1 and enter the credentials below.',
      title: 'Enter the project credentials below.',
    },
  ],
  submitLabel: 'Submit',
  title: 'Adobe Analytics',
} as const;

export const ANALYTICS_ONBOARDING_CONTENT = {
  benefits: [
    {
      icon: 'solid/chart-simple',
      text: 'View revenue numbers for your onsite galleries',
    },
    {
      icon: 'custom/regular-display-circle-dollar',
      text: 'View revenue for Like2Buy (our link-in-bio solution)',
    },
    {
      icon: 'regular/arrows-rotate',
      text: 'Automatic daily updates',
    },
  ],
  connectButtonLabel: 'Continue to Connect',
  description:
    'Connect your analytics provider to track full ROI and measure how UGC drives on-site conversions.',
  providerPrompt: 'Choose your analytics provider:',
  providers: {
    google: {
      name: 'Google Analytics',
      steps: [
        'Grant us access to Google Analytics',
        'Pick a property and add custom dimensions to it',
        'Link BigQuery',
      ],
    },
    adobe: {
      name: 'Adobe Analytics',
      steps: [
        'Create a project on Adobe Developer Console',
        'Reserve and setup an eVar',
        'Provide us the API credentials',
      ],
    },
  },
  setupGuide: {
    icon: 'regular/arrow-up-right-from-square',
    label: 'View Setup Guide',
    url: ANALYTICS_SETUP_GUIDE_URL,
  },
  stepsLabel: 'Steps preview:',
  title: 'See the revenue your content drives',
} as const;

export const ECOMMERCE_ATTRIBUTION_CONTENT = {
  helpText:
    'On-site e-commerce association settings are used to attribute e-commerce metrics to your Gallery or Review displays based on user-actions.',
  learnMoreLabel: 'Learn More',
  learnMoreUrl:
    'https://support.bazaarvoice.com/s/article/Ecommerce-Association-Settings-Overview?language=en_US',
  selectLabel: 'Show Ecommerce Metrics for site sessions where visitors:',
  selectWidth: '363px',
  title: 'Ecommerce Attribution',
  updatePermissionTooltip:
    "You don't have permission to complete this action. Contact a group admin if this is a mistake.",
} as const;

export enum ACCOUNT_SETTINGS_ROUTES {
  SOCIAL = 'account-settings.social',
  ANALYTICS = 'account-settings.analytics',
  TRACKING = 'account-settings.tracking',
  RIGHTS = 'account-settings.rights',
  USERS = 'account-settings.users',
  MOBILE = 'account-settings.mobile',
  EMAIL = 'account-settings.email',
  EMAIL_SETTINGS = 'account-settings.creator-email-settings',
  WALLETS = 'account-settings.wallets',
  PRODUCT = 'account-settings.product',
  ACCOUNT = 'account-settings.account',
  CONTENT_CURATION_GUIDELINES = 'account-settings.content-curation-guidelines',
}

export interface ISidebarPage {
  iconName?: UnisonIconType;
  state: ACCOUNT_SETTINGS_ROUTES;
  text: string;
}

export const SIDEBAR_PAGES: ISidebarPage[] = [
  {
    state: ACCOUNT_SETTINGS_ROUTES.SOCIAL,
    text: 'Social Media Accounts',
  },
  {
    state: ACCOUNT_SETTINGS_ROUTES.ANALYTICS,
    text: 'Analytics Providers',
  },
  {
    state: ACCOUNT_SETTINGS_ROUTES.TRACKING,
    text: 'Tracking Parameters',
  },
  {
    state: ACCOUNT_SETTINGS_ROUTES.RIGHTS,
    text: 'Rights Requests',
  },
  {
    state: ACCOUNT_SETTINGS_ROUTES.MOBILE,
    text: 'Mobile Notifications',
  },
  {
    state: ACCOUNT_SETTINGS_ROUTES.EMAIL_SETTINGS,
    text: 'Email Settings',
  },
  {
    state: ACCOUNT_SETTINGS_ROUTES.WALLETS,
    text: 'Campaigns Budget',
  },
  {
    state: ACCOUNT_SETTINGS_ROUTES.EMAIL,
    text: 'Email Reports',
  },
  {
    state: ACCOUNT_SETTINGS_ROUTES.PRODUCT,
    text: 'Product Catalog',
  },
  {
    state: ACCOUNT_SETTINGS_ROUTES.USERS,
    text: 'Users',
  },
  {
    state: ACCOUNT_SETTINGS_ROUTES.ACCOUNT,
    text: 'My Account',
  },
  {
    iconName: 'sparkles',
    state: ACCOUNT_SETTINGS_ROUTES.CONTENT_CURATION_GUIDELINES,
    text: 'Content Curation Guidelines',
  },
];

export enum EMPLOYEE_VIEW_OPTIONS {
  DEV = 'DEV',
  DOGFOOD = 'DOGFOOD',
  GAMMA = 'GAMMA',
  RELEASED = 'RELEASED',
  OPTINRELEASED = 'OPTINRELEASED',
  CLIENT_MODE = 'CLIENT_MODE',
}

export const ACCOUNT_WITH_NO_PERMISSIONS = "You don't have any permissions";
export const ACCOUNT_WITH_NO_DEFAULT_HANDLE_SET =
  ' Missing a main Instagram account. You will not have full functionalities until an account is set as main.';
export const IG_REFRESH_CONNECTION =
  'There is an issue with your Instagram connection. Please re-select your Instagram account.';
export const MAIN_ACCOUNT_PERMISSION_DETAILS =
  'The main Instagram account will have more permissions such as automatic publishing and access to insights.';
export const PERMISSIONS_TOOLTIP =
  'Your connected Facebook Page will give you access to certain permissions in the Bazaarvoice Vibe app. In order to access all permissions, you must have the role of Admin on your connected Facebook page and authorize Bazaarvoice to access your account.';
export const IMPACT_ACCOUNT_SID_TOOLTIP =
  'For each Impact account, this is the unique primary identifier. You can find it in the API access settings of your Impact account.';
export const IMPACT_AUTH_TOKEN_TOOLTIP =
  'The secret password key is exclusively paired with the Account SID, and you can find it in the API access settings of your Impact account.';
export const OPTIONTYPE = 'igAccount';

export const OAUTH_PERMISSION = 'admin:create_or_update_user';
export const OAUTH_NETWORK_PARAM = 'oauth-network';
export const OAUTH_ERROR_PARAM = 'oauth-error';
export const OAUTH_ERROR_MSG = 'oauth-msg';
export const USE_MODAL_FLOW = 'use-modal-flow';
export const OAUTH_YOUTUBE_PARAM = 'youtube';
